
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Store, Database, Rocket, MessageSquare, Eye, Globe, Zap, Users, Truck, Bike, Printer, Clock } from 'lucide-react';
import { Driver, SystemConfig, DeliveryProvider, Order } from '../types';
import { OracleSettings } from './settings/OracleSettings';
import { SettingsTabBusiness } from './settings/SettingsTabBusiness';
import { SettingsTabFleet } from './settings/SettingsTabFleet';
import { SettingsTabThirdParty } from './settings/SettingsTabThirdParty';
import { SettingsTabCommunication } from './settings/SettingsTabCommunication';
import { SettingsTabData } from './settings/SettingsTabData';
import { SettingsTabDeploy } from './settings/SettingsTabDeploy';
import { SettingsTabGoogle } from './settings/SettingsTabGoogle';
import { SettingsTabTeam } from './settings/SettingsTabTeam';
import { SettingsTabPrinter } from './settings/SettingsTabPrinter';
import { SettingsTabRetentionMonitor } from './settings/SettingsTabRetentionMonitor';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
  drivers: Driver[];
  setDrivers: React.Dispatch<React.SetStateAction<Driver[]>>;
  providers: DeliveryProvider[];
  setProviders: (providers: DeliveryProvider[]) => void;
  orders?: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  storeId?: string | null;
}

type SettingsTab = 'google' | 'business' | 'fleet' | 'third_party' | 'team' | 'oracle' | 'communication' | 'data' | 'deploy' | 'printer' | 'retention';

export const SettingsModal: React.FC<Props> = ({ isOpen, onClose, config, setConfig, drivers, setDrivers, providers, setProviders, storeId }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('business'); // Definir 'business' como aba ativa inicial
  const [isValidating, setIsValidating] = useState(false);

  const calculateOracleStress = useMemo(() => {
    const hubDemand: Record<number, number> = {};
    const operatingHours = config.operatingHours || {};
    for (let day = 0; day < 7; day++) {
      const ranges = operatingHours[day] || [];
      let totalMinutes = 0;
      ranges.forEach(range => {
        const [startH, startM] = range.start.split(':').map(Number);
        const [endH, endM] = range.end.split(':').map(Number);
        let diff = (endH * 60 + endM) - (startH * 60 + startM);
        if (diff < 0) diff += 24 * 60;
        totalMinutes += diff;
      });
      hubDemand[day] = totalMinutes;
    }
    const fleetSupply: Record<number, number> = {};
    for (let day = 0; day < 7; day++) fleetSupply[day] = 0;
    drivers.forEach(driver => {
      if (driver.status === 'offline' && !driver.schedule) return;
      const schedule = driver.schedule;
      if (!schedule) return;
      schedule.activeDays.forEach(day => {
        schedule.shifts.forEach(shift => {
          const [startH, startM] = shift.start.split(':').map(Number);
          const [endH, endM] = shift.end.split(':').map(Number);
          let diff = (endH * 60 + endM) - (startH * 60 + startM);
          if (diff < 0) diff += 24 * 60;
          fleetSupply[day] += diff;
        });
      });
    });
    const weights: Record<number, number> = {};
    let totalStress = 0;
    let count = 0;
    for (let day = 0; day < 7; day++) {
      const demand = hubDemand[day] || 0;
      const supply = fleetSupply[day] || 0;
      if (demand === 0) weights[day] = 0.5;
      else if (supply === 0) weights[day] = 2.5;
      else weights[day] = demand / supply;
      if (demand > 0) { totalStress += weights[day]; count++; }
    }
    const avgStress = count > 0 ? totalStress / count : 1;
    for (let day = 0; day < 7; day++) {
      if (hubDemand[day] > 0) weights[day] = parseFloat((weights[day] / avgStress).toFixed(1));
      else weights[day] = 0.1;
    }
    return weights;
  }, [config.operatingHours, drivers]);

  const updateConfigWithAutoCosts = (updates: Partial<SystemConfig>) => {
    const dailyCost = updates.dailyFixedCost !== undefined ? updates.dailyFixedCost : config.dailyFixedCost;
    const shiftHours = updates.averageShiftHours !== undefined ? updates.averageShiftHours : (config.averageShiftHours || 8);
    const safeShiftHours = shiftHours > 0 ? shiftHours : 8;
    const baseMinuteCost = dailyCost / (safeShiftHours * 60);
    setConfig({
      ...config,
      ...updates,
      costPerMinuteLowDemand: parseFloat((baseMinuteCost * 0.1).toFixed(4)),
      costPerMinuteHighDemand: parseFloat((baseMinuteCost * 3.0).toFixed(4))
    });
  };

  const tabs = [
    { id: 'business', icon: Store, label: "Hub & Negócio" }, // Movido para o início
    { id: 'google', icon: Globe, label: "Integrações" },
    { id: 'fleet', icon: Bike, label: "Frota Própria" },
    { id: 'third_party', icon: Truck, label: "Apps Parceiros" },
    { id: 'team', icon: Users, label: "Gestão Equipe" },
    { id: 'oracle', icon: Eye, label: "O Oráculo" },
    { id: 'retention', icon: Clock, label: "Alertas" },
    { id: 'communication', icon: MessageSquare, label: "Comunicação" },
    { id: 'printer', icon: Printer, label: "Impressão" },
    { id: 'data', icon: Database, label: "Gestão de Dados" },
    { id: 'deploy', icon: Rocket, label: "Diagnóstico" }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 md:p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-sal-gunmetal/90 backdrop-blur-md" />
        <motion.div
          initial={{ scale: 0.98, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0, x: isValidating ? [0, -5, 5, -5, 5, 0] : 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          exit={{ scale: 0.98, opacity: 0, y: 10 }}
          className="relative bg-card-bg w-full max-w-6xl h-[95vh] md:h-[90vh] rounded-3xl shadow-lg border border-border-dim overflow-hidden flex flex-col md:flex-row"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-full md:w-64 bg-app-bg border-b md:border-b-0 md:border-r border-border-dim flex flex-col shrink-0">
            <div className="p-6 border-b border-border-dim bg-gradient-to-br from-app-bg to-card-bg/30 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-sal-cyber-teal rounded-xl shadow-lg shadow-cyan-900/10">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-black text-text-primary leading-tight uppercase tracking-tighter text-base">Central</h2>
                  <p className="text-xs text-sal-cyber-teal/60 font-black uppercase tracking-widest">S.A.L. ROTA v3.4</p>
                </div>
              </div>
            </div>
            <div className="p-3 flex-1 overflow-x-auto md:overflow-y-auto flex md:flex-col gap-1 no-scrollbar">
              {tabs.map((tab) => (
                <button
                  type="button"
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as SettingsTab)}
                  className={`flex items-center px-4 py-3.5 text-xs font-black transition-all rounded-xl shrink-0 group ${activeTab === tab.id
                    ? 'bg-sal-cyber-teal text-white shadow-lg translate-x-1'
                    : 'text-text-secondary hover:bg-sal-cyber-teal/10 hover:text-sal-cyber-teal'
                    }`}
                >
                  <tab.icon className={`w-4 h-4 mr-3 transition-colors ${activeTab === tab.id ? 'text-white' : 'text-text-muted group-hover:text-sal-cyber-teal'}`} />
                  <span className="whitespace-nowrap uppercase tracking-widest">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 flex flex-col h-full bg-app-bg/50 relative overflow-hidden">
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar configurações"
              className="absolute top-4 right-6 z-20 p-2.5 bg-card-bg/50 hover:bg-card-bg rounded-full transition-all text-text-muted hover:text-text-primary border border-border-dim active:scale-95"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 overflow-y-auto p-6 md:p-10 pb-32 min-h-0 no-scrollbar">
              {activeTab === 'google' && <SettingsTabGoogle config={config} setConfig={setConfig} providers={providers} storeId={storeId} />}
              {activeTab === 'business' && <SettingsTabBusiness config={config} setConfig={setConfig} />}
              {activeTab === 'fleet' && <SettingsTabFleet config={config} setConfig={setConfig} drivers={drivers} setDrivers={setDrivers} updateConfigWithAutoCosts={updateConfigWithAutoCosts} />}
              {activeTab === 'third_party' && <SettingsTabThirdParty config={config} setConfig={setConfig} providers={providers} setProviders={setProviders} />}
              {activeTab === 'team' && <SettingsTabTeam storeId={storeId || ''} drivers={drivers} />}
              {activeTab === 'oracle' && <OracleSettings config={config} oracleConfig={config.oracle} onChange={(newOracleConfig) => setConfig({ ...config, oracle: newOracleConfig })} isLocked={drivers.length === 0} recommendedWeights={calculateOracleStress} onReset={() => setConfig({ ...config, oracle: { ...config.oracle, dailyWeights: calculateOracleStress, isManualOverride: false, active: true, hourlyShare: config.oracle?.hourlyShare || [], averageDailyOrders: config.oracle?.averageDailyOrders || 0 } })} />}
              {activeTab === 'retention' && <SettingsTabRetentionMonitor config={config} setConfig={setConfig} />}
              {activeTab === 'communication' && <SettingsTabCommunication config={config} setConfig={setConfig} />}
              {activeTab === 'printer' && <SettingsTabPrinter config={config} setConfig={setConfig} />}
              {activeTab === 'data' && <SettingsTabData />}
              {activeTab === 'deploy' && <SettingsTabDeploy config={config} setConfig={setConfig} />}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-card-bg border-t border-border-dim flex justify-end gap-3 z-30 shrink-0 shadow-lg">
              <button
                type="button"
                onClick={onClose}
                aria-live="polite"
                className="w-full md:w-auto px-12 py-4 bg-sal-cyber-teal text-white font-black rounded-2xl shadow-lg hover:brightness-110 active:scale-[0.98] transition-all uppercase tracking-widest text-sm"
              >
                Confirmar e Aplicar
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
